import { add, format } from 'date-fns';
import { useEffect, useState } from 'react';
import { QueryObserverResult } from 'react-query';

import { ETPayButton } from '../../../../components/button';
import { Input } from '../../../../components/input';
import { Modal } from '../../../../components/modal';
import { Spinner } from '../../../../components/spinner';
import { Tabs } from '../../../../components/tabs';
import { useFloatingAlert } from '../../../../hooks/useFloatingAlert';
import { useMerchant } from '../../../../hooks/useMerchant';
import { PaymentMultiuse } from '../../../../models/payment-links-multiuse';
import {
	ExpirationTypesAllowed,
	TypesAllowed,
	ViewsAllowed,
} from '../../../../models/payment-links-multiuse';
import { PaymentLinksMultiuseServices } from '../../../../services/payment-links-multiuse';
import { validatePermission } from '../../../../utils/common';
import { transformStringToDate } from '../../../../utils/dates';
import { errorFormatter } from '../../../../utils/errors';
import { ExportModalContent, ExportModalDescription, ExportModalTitle } from '../table/styles';
import { CreatePaymentLink } from './components/create-payment-link';
import { QRTab } from './components/qr-tab';
import { SendLinkTab } from './components/send-link-tab';
import {
	Button,
	ContainerInput,
	CopyButton,
	ModalBody,
	ModalContainer,
	ModalFooter,
	Subtitle,
	TabsContainer,
	Title,
	TitleSpan,
} from './styles';
interface NewSinglePaymentProps {
	show: boolean;
	onClose: () => void;
	selectedToEdit?: PaymentMultiuse;
	viewType: ViewsAllowed;
	refetch: () => Promise<QueryObserverResult<any, unknown>>;
}

export interface Values {
	amount: string;
	reference: string;
	description: string;
	paymentType: TypesAllowed;
	expirationType: ExpirationTypesAllowed;
	initExpirationDate?: string;
	initExpirationTime: string;
	endExpirationDate?: string;
	endExpirationTime?: string;
	numPayments: number;
	buttonDisabled: boolean;
	selectButtonDisabled: boolean;
	showConfigExpiration: boolean;
	loading: boolean;
	error: boolean;
	errorMessage?: string;
	retryFunction?: () => void;
}

const defaultValues: Values = {
	amount: '',
	reference: '',
	description: '',
	paymentType: 'unique',
	expirationType: 'date',
	initExpirationDate: format(new Date(), 'dd/MM/yyyy'),
	initExpirationTime: format(new Date(), 'HH:mm'),
	endExpirationDate: format(add(new Date(), { days: 1 }), 'dd/MM/yyyy'),
	endExpirationTime: format(add(new Date(), { days: 1 }), 'HH:mm'),
	numPayments: 1,
	buttonDisabled: true,
	selectButtonDisabled: true,
	showConfigExpiration: true,
	loading: false,
	error: false,
	errorMessage: '',
};

interface Response {
	pl_qr: string;
	pl_url: string;
}

const defaultResponse: Response = { pl_qr: '', pl_url: '' };
type CopyText = 'Copiar' | 'Copiado';

const NewSinglePayment = ({
	show,
	onClose,
	selectedToEdit,
	viewType,
	refetch,
}: NewSinglePaymentProps) => {
	const { merchantSelected } = useMerchant();
	const { handleShowAlert } = useFloatingAlert();
	const [view, setView] = useState<ViewsAllowed>(viewType);
	const [state, setState] = useState<Values>({
		...defaultValues,
		reference: selectedToEdit?.merchantorderid || '',
		description: selectedToEdit?.description || '',
		amount: selectedToEdit?.amount.replace('$', '') || '',
	});
	const [countRetry, setCountRetry] = useState<number>(0);
	const [copyText, setCopyText] = useState<CopyText>('Copiar');
	const [response, setResponse] = useState<Response>({
		...defaultResponse,
		pl_url: selectedToEdit?.token || '',
		pl_qr: selectedToEdit?.token_qr || '',
	});

	const addCountRetry = () => {
		setCountRetry((prevCount) => prevCount + 1);
	};

	const resetCountRetry = () => {
		setCountRetry(0);
	};

	const handleCloseModal = () => {
		//Reset default values but with updated new Date
		setState({
			...defaultValues,
			initExpirationDate: format(new Date(), 'dd/MM/yyyy'),
			initExpirationTime: format(new Date(), 'HH:mm'),
			endExpirationDate: format(add(new Date(), { days: 1 }), 'dd/MM/yyyy'),
			endExpirationTime: format(add(new Date(), { days: 1 }), 'HH:mm'),
		});
		resetCountRetry();
		onClose();
		refetch();
	};

	const handleContinueAction = async () => {
		setState({ ...state, loading: true });
		let _from = '';
		let _to = '';

		try {
			if (state.expirationType === 'date') {
				const from = transformStringToDate(
					state.initExpirationDate as string,
					state.initExpirationTime,
				);
				_from = `${format(new Date(from), "yyyy-MM-dd'T'HH:mm")}`;
				const to = transformStringToDate(
					state.endExpirationDate as string,
					state.endExpirationTime as string,
				);
				_to = `${format(new Date(to), "yyyy-MM-dd'T'HH:mm")}`;
			}
			const { pl_multiuse_qr, pl_multiuse_url } =
				await PaymentLinksMultiuseServices.newPaymentLinkMultiuse({
					merchantCode: merchantSelected?.code as string,
					paymentType: state.paymentType,
					amount: +state.amount.replaceAll(',', ''),
					reference: state.reference,
					description: state.description,
					numPayments:
						state.paymentType === 'recurrent' && state.expirationType === 'num_payments'
							? +state.numPayments
							: undefined,
					from: state.expirationType === 'date' ? _from : undefined,
					to: state.expirationType === 'date' ? _to : undefined,
					expirationType: state.expirationType,
				});
			setResponse({ pl_qr: pl_multiuse_qr, pl_url: pl_multiuse_url });
			setState({ ...state, loading: false });
			resetCountRetry();
			handleShowAlert({
				message: 'Enlace de cobro creado',
				type: 'success',
			});
			setView('share-payment-link');
		} catch (e: any) {
			handleShowAlert({
				message: errorFormatter(e.code),
				type: 'error',
			});
			addCountRetry();
			setCountRetry((prevCount) => {
				if (prevCount < 3) {
					setState({
						...state,
						loading: false,
						error: true,
						errorMessage:
							'Hubo un error al intentar crear el link de cobro. Por favor, inténtalo nuevamente.',
						retryFunction: handleContinueAction,
					});
				} else {
					setState({
						...state,
						loading: false,
						error: true,
						errorMessage: 'Lo sentimos, superó la cantidad de reintentos.',
						retryFunction: handleCloseModal,
					});
				}
				return prevCount; // Devolvemos el valor del contador para mantener su estado actualizado
			});
		}
	};

	const handleEditPaymentLink = async () => {
		setState((state) => ({ ...state, loading: true }));
		try {
			await PaymentLinksMultiuseServices.editPaymentLinkMultiuse({
				merchantCode: merchantSelected?.code as string,
				reference: state.reference,
				token: selectedToEdit?.token.split('/').slice(-1)[0],
				description: state.description,
			});
			setView('share-payment-link');
			resetCountRetry();
			handleShowAlert({
				message: 'Enlace de cobro modificado',
				type: 'success',
			});
			setState((state) => ({ ...state, loading: false }));
		} catch (e: any) {
			handleShowAlert({
				message: errorFormatter(e.code),
				type: 'error',
			});
			addCountRetry();
			setCountRetry((prevCount) => {
				if (prevCount < 3) {
					setState((state) => ({
						...state,
						loading: false,
						error: true,
						errorMessage:
							'Hubo un error al intentar editar el link de cobro. Por favor, inténtalo nuevamente.',
						retryFunction: handleEditPaymentLink,
					}));
				} else {
					setState((state) => ({
						...state,
						loading: false,
						error: true,
						errorMessage: 'Lo sentimos, superó la cantidad de reintentos.',
						retryFunction: handleCloseModal,
					}));
				}
				return prevCount; // Devolvemos el valor del contador para mantener su estado actualizado
			});
		}
	};

	//Use effect to mount the component
	useEffect(() => {
		//Update the time in the default values
		setState({
			...state,
			initExpirationTime: format(new Date(), 'HH:mm'),
			endExpirationTime: format(add(new Date(), { days: 1 }), 'HH:mm'),
		});
	}, []);

	const renderView = () => {
		switch (view) {
			case 'create-payment':
				return 'Crear enlace de cobro';
			case 'edit-payment-link':
				return 'Editar enlace de cobro';
			case 'share-payment-link':
				return 'Compartir enlace de cobro';
		}
	};

	const renderContentModalByView = () => {
		switch (view) {
			case 'create-payment':
				return (
					<CreatePaymentLink
						state={state}
						setState={setState}
						selectedToEdit={selectedToEdit}
						view={view}
					/>
				);
			case 'edit-payment-link':
				return (
					<CreatePaymentLink
						state={state}
						setState={setState}
						selectedToEdit={selectedToEdit}
						view={view}
					/>
				);
			case 'share-payment-link':
				return (
					<>
						<header>
							<Title>
								{state.reference} <TitleSpan> por </TitleSpan>${state.amount}
							</Title>
							<Subtitle>Comparte el enlace para comenzar a recibir pagos</Subtitle>
						</header>
						<ContainerInput>
							<Input
								label="Enlace de cobro"
								name="paymentLink"
								defaultValue={response.pl_url}
								variant="large"
								readOnly
								rightIcon={<CopyButton>{copyText}</CopyButton>}
								rightIconClick={async () => {
									await navigator.clipboard.writeText(response.pl_url);
									setCopyText('Copiado');
									setTimeout(() => setCopyText('Copiar'), 2500);
								}}
							/>
						</ContainerInput>
						<TabsContainer>
							<Tabs
								tabs={[
									{
										reference: 'send-link',
										title: 'Enviar enlace',
										show: validatePermission('PL_SEND_SMS'),
									},
									{
										reference: 'qr',
										title: 'Código QR',
										show: validatePermission('PL_SHARE_LINK'),
									},
								]}
								defaultReferenceSelected="send-link"
							>
								<SendLinkTab id="send-link" paymentLinkUrl={response.pl_url} />
								<QRTab
									id="qr"
									amount={state.amount}
									merchantName={merchantSelected?.name as string}
									paymentLinkUrl={response.pl_qr}
								/>
							</Tabs>
						</TabsContainer>
					</>
				);
		}
	};

	const renderContentModalByState = () => {
		if (state.loading) {
			return (
				<Modal
					show={show}
					type="medium"
					title="Cargando"
					allowCloseModal={false}
					onClose={handleCloseModal}
				>
					<Spinner />
				</Modal>
			);
		} else if (state.error) {
			return (
				<Modal show={show} type="medium" title="Error" onClose={handleCloseModal}>
					<ExportModalContent>
						<ExportModalTitle>Ha ocurrido un error</ExportModalTitle>
						<ExportModalDescription>
							{state.errorMessage ||
								'Lo sentimos, hubo un error con el proceso. Por favor, inténtalo nuevamente.'}
						</ExportModalDescription>
						<ETPayButton
							title={countRetry < 3 ? 'Reintentar' : 'Cerrar'}
							color="secondary"
							onClick={state.retryFunction}
						/>
					</ExportModalContent>
				</Modal>
			);
		} else {
			return (
				<Modal
					show={show}
					type="medium"
					title={renderView()}
					onClose={handleCloseModal}
					allowCloseModal={true}
				>
					<ModalContainer>
						<ModalBody>{renderContentModalByView()}</ModalBody>

						<ModalFooter>
							{(view == 'create-payment' || view == 'edit-payment-link') && (
								<Button
									onClick={selectedToEdit ? handleEditPaymentLink : handleContinueAction}
									disabled={selectedToEdit ? state.buttonDisabled : state.buttonDisabled}
								>
									{selectedToEdit ? 'Guardar cambios' : 'Crear enlace de cobros'}
								</Button>
							)}
						</ModalFooter>
					</ModalContainer>
				</Modal>
			);
		}
	};
	return <>{renderContentModalByState()}</>;
};

export { NewSinglePayment };
